import { hash32 } from '@vm/shared';
import { M } from '../palette';
import { blocks, MapBuilder, solid, STAIR_LAND, STAIR_RISE, STAIR_TOP, STAIR_TREAD, symPoint, symYaw, yawToward, type Brush, type DoorSpec } from './builder';
import { emptyMeta, type MapDef, type MapMeta } from './types';

const S = 640;
const G = 32;
const FH = 30;
const SYM = 'rotateX' as const;

/** Roof walking height (first air) of a flat-roofed building with `floors` floors. */
const roofY = (floors: number): number => G - 1 + floors * FH + 2;

const ICONS: Record<string, string[]> = {
  heart: ['.xx.xx.', 'xxxxxxx', 'xxxxxxx', '.xxxxx.', '..xxx..', '...x...'],
  star: ['...x...', '..xxx..', 'xxxxxxx', '.xxxxx.', '.xx.xx.', 'x.....x'],
  note: ['..xxxx', '..x..x', '..x..x', '..x..x', 'xxx.xx', 'xx..xx'],
  bolt: ['...xx', '..xx.', '.xxxx', '..xx.', '.xx..', 'xx...'],
  cup: ['.x.x..', '..x.x.', 'xxxxx.', 'xxxxxx', 'xxxx.x', '.xxx..'],
  bowl: ['.x..x.', '..x..x', 'xxxxxx', 'xxxxxx', '.xxxx.', '..xx..'],
  planet: ['..xxx...', '.xxxxx..', 'xxxxxxxx', '.xxxxx..', '..xxx...'],
  rocket: ['..x..', '.xxx.', '.xxx.', '.xxx.', 'xxxxx', 'x.x.x'],
  cat: ['x...x', 'xx.xx', 'xxxxx', 'x.x.x', 'xxxxx', '.xxx.'],
  game: ['.xxxxx.', 'xx.x.xx', 'x.xxx.x', 'xx.x.xx', '.xxxxx.'],
};

/**
 * Neon pictogram: a dark plate replacing the wall's outer layer (x or z is that layer) with the
 * glowing pixels one voxel in front of it, facing `face`.
 */
function neonSign(b: MapBuilder, x: number, y: number, z: number, face: 'n' | 's' | 'e' | 'w', icon: string, color: number, px = 2): void {
  const rows = ICONS[icon];
  const w = rows.reduce((m, r) => Math.max(m, r.length), 0) * px;
  const h = rows.length * px;
  const back = M('paint_black');
  const ink = { x: color };
  if (face === 's') {
    b.box(x - 2, y - h - 1, z, x + w + 2, y + 3, z + 1, back);
    b.glyph(x, y, z + 1, 's', rows, ink, px);
  } else if (face === 'n') {
    b.box(x - w - 1, y - h - 1, z, x + 3, y + 3, z + 1, back);
    b.glyph(x, y, z - 1, 'n', rows, ink, px);
  } else if (face === 'e') {
    b.box(x, y - h - 1, z - w - 1, x + 1, y + 3, z + 3, back);
    b.glyph(x + 1, y, z, 'e', rows, ink, px);
  } else {
    b.box(x, y - h - 1, z - 2, x + 1, y + 3, z + w + 2, back);
    b.glyph(x - 1, y, z, 'w', rows, ink, px);
  }
}

interface TowerSpec {
  x0: number;
  z0: number;
  x1: number;
  z1: number;
  floors: number;
  wall: Brush;
  trim: number;
  doors: DoorSpec[];
  /** Side of the roof-access stair hut. */
  hatch?: 'n' | 's';
  parapet?: number;
}

/**
 * City building: floors with stairs, a flat parapet roof reached by a final stair flight and a
 * stair hut, and a skin of lit and dark window panels (opaque, cheap to render).
 */
function tower(b: MapBuilder, t: TowerSpec): number {
  const { x0, z0, x1, z1, floors } = t;
  b.building({
    x0, z0, x1, z1, y0: G - 1, floors, floorH: FH, wall: t.wall, trim: t.trim, band: t.trim, roofStyle: 'flat', parapet: t.parapet ?? 4,
    floor: solid([M('concrete_3')]), windows: null, doors: t.doors,
  });
  const roof = roofY(floors);
  // Window panels on the outer wall layer: dark glass, warm lights, a few cool TV glows.
  const lit = [M('neon_orange'), M('neon_yellow'), M('neon_orange'), M('neon_cyan')];
  const dark = M('tile_black');
  for (let f = 0; f < floors; f++) {
    const wy = G + f * FH + 10;
    for (let x = x0 + 8; x + 6 <= x1 - 6; x += 12)
      for (const z of [z0, z1 - 1]) {
        if (f === 0 && t.doors.some((d) => (d.side === 'n' ? z === z0 : d.side === 's' ? z === z1 - 1 : false))) continue;
        const h = hash32(x, wy, z, 99);
        b.box(x, wy, z, x + 6, wy + 11, z + 1, h % 100 < 38 ? lit[h % 4] : dark);
      }
    for (let z = z0 + 8; z + 6 <= z1 - 6; z += 12)
      for (const x of [x0, x1 - 1]) {
        if (f === 0 && t.doors.some((d) => (d.side === 'w' ? x === x0 : d.side === 'e' ? x === x1 - 1 : false))) continue;
        const h = hash32(x, wy, z, 98);
        b.box(x, wy, z, x + 1, wy + 11, z + 6, h % 100 < 38 ? lit[h % 4] : dark);
      }
  }
  // Last flight up to the roof, under a hut over the hatch: a 0.8 m landing at its foot, the flight
  // under an opening in the roof slab, and a 1 m landing at the top inside the hut, whose side door
  // opens onto the roof.
  if (floors > 1 || t.hatch) {
    const north = t.hatch ? t.hatch === 'n' : (floors - 1) % 2 === 0;
    const fy = G + (floors - 1) * FH;
    const W = 12;
    const sz0 = north ? z0 + 2 : z1 - 2 - W;
    const sz1 = sz0 + W;
    const rise = roof - fy;
    const xTop = x1 - 13;
    const sx0 = xTop - rise + 1;
    // Gentle stairs (0.2 m rise, 0.3 m tread) end where the old 45 degree flight did, so the first step is further back (climbEnding). The roof slab
    // (its underside at roof - 2) is in the way from the first step that has less than 1.8 m over it; the opening starts 8 voxels before that step (the
    // body is 6 voxels wide, so its box would clip the slab edge while stepping up from the step before).
    const steps = Math.ceil(rise / STAIR_RISE);
    const first = sx0 - (steps * STAIR_TREAD - rise);
    const xo = first + STAIR_TREAD * (steps - Math.ceil(20 / STAIR_RISE)) - 8;
    b.clear(xo, roof - 3, sz0, xTop + 1, roof, sz1);
    b.climbEnding(sx0, fy, sz0, 'e', W, rise, solid([M('concrete_2')]));
    const hz0 = north ? z0 : sz0 - 2;
    const hz1 = north ? sz1 + 2 : z1;
    b.walls(xo - 2, roof, hz0, x1, roof + 21, hz1, 2, t.wall);
    b.box(xo - 2, roof + 21, hz0, x1, roof + 23, hz1, t.trim);
    // The hut's west wall stands right on the roof slab, over the lowest steps: that left 2.2 m of headroom where a step up needs 2.3 m (a
    // bot is 1.8 m tall and steps 0.5 m), so bots could not walk down from the roof and every rooftop team spawn was sealed in. Open the
    // wall over the stair with a doorway whose lintel is 0.3 m above the roof.
    b.clear(xo - 2, roof - 3, sz0, xo, roof + 3, sz1);
    if (north) b.clear(xTop - 4, roof, hz1 - 2, x1 - 2, roof + 19, hz1);
    else b.clear(xTop - 4, roof, hz0, x1 - 2, roof + 19, hz0 + 2);
    b.box(xTop - 6, roof + 19, north ? hz1 : hz0 - 1, x1, roof + 20, north ? hz1 + 1 : hz0, M('neon_green'));
  }
  return roof;
}

/**
 * Steel catwalk bridge with railings; deck top at y - 1. `alongX` is the direction people walk across it: the side rails run that
 * way and the ends stay open. (Guessing it from the longer side is wrong for a short bridge: a 1.6 m x 1.0 m link between two roofs
 * got rails across both ends, and the 3.0 m x 3.2 m bridges at the map's centre got a rail across the middle of the deck, which
 * sealed each team onto its own half of the city.)
 */
function catwalk(b: MapBuilder, x0: number, z0: number, x1: number, z1: number, y: number, alongX: boolean = x1 - x0 > z1 - z0): void {
  b.box(x0, y - 2, z0, x1, y, z1, M('steel_dark_2'));
  const rail = M('metal_1');
  if (alongX) {
    b.box(x0, y + 9, z0, x1, y + 10, z0 + 1, rail);
    b.box(x0, y + 9, z1 - 1, x1, y + 10, z1, rail);
    for (let x = x0; x < x1; x += 8) {
      b.box(x, y, z0, x + 1, y + 9, z0 + 1, rail);
      b.box(x, y, z1 - 1, x + 1, y + 9, z1, rail);
    }
    b.clear(x0, y, z0 + 1, x1, y + 22, z1 - 1);
  } else {
    b.box(x0, y + 9, z0, x0 + 1, y + 10, z1, rail);
    b.box(x1 - 1, y + 9, z0, x1, y + 10, z1, rail);
    for (let z = z0; z < z1; z += 8) {
      b.box(x0, y, z, x0 + 1, y + 9, z + 1, rail);
      b.box(x1 - 1, y, z, x1, y + 9, z + 1, rail);
    }
    b.clear(x0 + 1, y, z0, x1 - 1, y + 22, z1);
  }
}

function acUnit(b: MapBuilder, x: number, y: number, z: number): void {
  b.box(x, y, z, x + 14, y + 9, z + 10, M('metal_3'));
  b.cylinder(x + 7, z + 5, y + 9, y + 10, 3.5, M('steel_dark_1'));
  b.box(x + 6, y + 9, z + 4, x + 8, y + 10, z + 6, M('metal_1'));
}

function waterTank(b: MapBuilder, x: number, y: number, z: number): void {
  for (const [dx, dz] of [[-7, -7], [6, -7], [-7, 6], [6, 6]]) b.box(x + dx, y, z + dz, x + dx + 2, y + 22, z + dz + 2, M('steel_dark_1'));
  b.line(x - 7, y + 2, z - 7, x + 7, y + 20, z - 7, M('steel_dark_1'));
  b.line(x - 7, y + 2, z + 7, x + 7, y + 20, z + 7, M('steel_dark_1'));
  b.box(x - 10, y + 22, z - 10, x + 10, y + 24, z + 10, M('planks_dark_1'));
  b.cylinder(x, z, y + 24, y + 48, 10, M('planks_4'));
  for (const yy of [y + 29, y + 37, y + 45]) b.cylinder(x, z, yy, yy + 1, 10.4, M('steel_dark_2'), 9.4);
  b.cone(x, y + 48, z, 11, 8, M('roof_slate'));
}

function antenna(b: MapBuilder, x: number, y: number, z: number, h: number): void {
  b.box(x, y, z, x + 2, y + h, z + 2, M('steel_dark_1'));
  for (let k = 1; k < 4; k++) b.box(x - 4 + k, y + (h * k) / 4, z, x + 6 - k, y + (h * k) / 4 + 1, z + 2, M('steel_dark_1'));
  b.box(x, y + h, z, x + 2, y + h + 2, z + 2, M('neon_red'));
}

/** Rooftop billboard on steel legs: a glowing panel with a pictogram. */
function billboard(b: MapBuilder, x0: number, y: number, z: number, len: number, panel: number, icon: string, ink: number): void {
  const legs = M('steel_dark_1');
  for (const x of [x0 + 4, x0 + len - 6]) {
    b.box(x, y, z, x + 2, y + 44, z + 2, legs);
    b.line(x + 1, y, z + 12, x + 1, y + 30, z + 1, legs);
  }
  b.box(x0, y + 18, z - 1, x0 + len, y + 46, z, panel);
  b.box(x0 - 1, y + 17, z, x0 + len + 1, y + 47, z + 1, M('steel_dark_2'));
  const rows = ICONS[icon];
  const w = rows[0].length * 3;
  b.glyph(x0 + (len >> 1) + (w >> 1) - 1, y + 41, z - 2, 'n', rows, { x: ink }, 3);
  b.box(x0, y + 46, z - 2, x0 + len, y + 47, z - 1, M('neon_yellow'));
}

/** Street lamp with a warm head. */
function streetLamp(b: MapBuilder, x: number, z: number, dx: number, dz: number): void {
  b.box(x, G, z, x + 2, G + 36, z + 2, M('steel_dark_1'));
  b.box(Math.min(x, x + dx * 6), G + 36, Math.min(z, z + dz * 6), Math.max(x + 2, x + 2 + dx * 6), G + 38, Math.max(z + 2, z + 2 + dz * 6), M('steel_dark_1'));
  b.box(x + dx * 6 - 1, G + 34, z + dz * 6 - 1, x + dx * 6 + 3, G + 36, z + dz * 6 + 3, M('neon_yellow'));
}

/**
 * Stairs between two roofs across a 2.0 m alley: a U-shaped flight of two 1.0 m lanes, because a gentle flight (0.2 m rise, 0.3 m tread) up the 6 m between the
 * roofs is 9 m long and the alley is only 2 m wide. Lane A climbs from the foot (a steel deck over the alley at the low roof's level, entered through a gap in the
 * low roof's parapet) north to a landing; lane B climbs back south beside it to a deck at the high roof's level, which leads through a gap in the high roof's
 * parapet. The flights are open steel plates (3 voxels thick) with a plate for the landing, and hang between the two walls like the old flight did.
 * `x0`..`x1` is the alley (the high building is west of it, the low one east); `zf` is the first step of lane A (its foot is the 2.0 m south of it).
 */
function alleyStairs(b: MapBuilder, a: { x0: number; x1: number; zf: number; low: number; high: number }): void {
  const lane = (a.x1 - a.x0) >> 1;
  const steel = solid([M('metal_2')]);
  const climb = a.high - a.low;
  const steps = Math.ceil(climb / STAIR_RISE);
  const nA = Math.ceil(steps / 2);
  const nB = steps - nA;
  const hA = nA * STAIR_RISE - (steps * STAIR_RISE - climb);
  const hB = climb - hA;
  // Foot: a deck across the whole alley (so nobody steps off into it) where the low roof's parapet is opened.
  b.box(a.x0, a.low - 2, a.zf + 1, a.x1, a.low, a.zf + 1 + 2 * (STAIR_TOP + 1), steel);
  b.clear(a.x1 - 1, a.low, a.zf + 1, a.x1 + 2, a.low + 6, a.zf + 1 + 2 * (STAIR_TOP + 1));
  // Lane A beside the low building, the landing plate at its far end, lane B beside the high building climbing back.
  b.flight(a.x1 - lane, a.low, a.zf, 'n', lane, hA, steel, STAIR_RISE, STAIR_TREAD, 3);
  const endA = a.zf - nA * STAIR_TREAD + 1;
  b.box(a.x0, a.low + hA - 3, endA - STAIR_LAND, a.x1, a.low + hA, endA, steel);
  const laneB = b.flight(a.x0, a.low + hA, endA, 's', lane, hB, steel, STAIR_RISE, STAIR_TREAD, 3);
  laneB.landing = true;
  // Top: a deck at the high roof's level straight ahead of lane B, through the high roof's parapet.
  const zt = endA + nB * STAIR_TREAD;
  b.box(a.x0 - 2, a.high - 2, zt, a.x0 + lane, a.high, zt + STAIR_TOP, steel);
  b.clear(a.x0 - 2, a.high, zt, a.x0 + 1, a.high + 6, zt + STAIR_TOP);
}

function westHalf(b: MapBuilder): void {
  // Streets: sidewalks everywhere, then asphalt and markings.
  b.box(0, G - 1, 0, 320, G, S, blocks([M('concrete_2')], 5, 2));
  b.box(0, G - 1, 198, 320, G, 242, solid([M('asphalt_1')]));
  b.box(298, G - 1, 0, 320, G, S, solid([M('asphalt_1')]));
  for (let x = 6; x < 290; x += 24) b.box(x, G - 1, 219, x + 12, G, 221, M('road_yellow'));
  for (let z = 6; z < S; z += 24) if (z < 190 || z > 250) b.box(318, G - 1, z, 320, G, z + 12, M('road_white'));
  // Crosswalk stripes.
  for (let z = 200; z < 240; z += 6) b.box(284, G - 1, z, 296, G, z + 3, M('paint_white'));

  // North-west block.
  const A = tower(b, { x0: 16, z0: 16, x1: 126, z1: 120, floors: 4, wall: blocks([M('brick_gray')], 3, 0), trim: M('concrete_4'), doors: [{ side: 's', w: 12 }] });
  const B = tower(b, { x0: 146, z0: 16, x1: 290, z1: 110, floors: 2, parapet: 2, wall: solid([M('plaster_2')]), trim: M('concrete_4'), doors: [{ side: 'e', w: 12 }, { side: 's', w: 12 }] });
  tower(b, { x0: 16, z0: 130, x1: 126, z1: 188, floors: 1, wall: blocks([M('brick_red_2')], 3, 0), trim: M('stone_4'), doors: [{ side: 's', w: 14 }] });
  const D = tower(b, { x0: 146, z0: 120, x1: 290, z1: 188, floors: 2, parapet: 2, wall: blocks([M('brick_red_1')], 3, 0), trim: M('stone_2'), doors: [{ side: 's', w: 12 }, { side: 'e', w: 12 }], hatch: 's' });
  // Noodle bar awning and sign.
  b.box(14, G + 22, 188, 128, G + 23, 198, M('cloth_red'));
  neonSign(b, 84, G + 19, 187, 's', 'bowl', M('neon_orange'));
  // Rooftop garden on D.
  // (The third planter stands at z 148: at z 160 its edge reached into the roof hut's stair opening and left 0.4 m of head room over two columns of the flight.)
  for (const [x, z] of [[170, 140], [220, 140], [260, 148]]) {
    b.box(x, D, z, x + 16, D + 5, z + 16, M('brick_gray'));
    b.box(x + 1, D + 4, z + 1, x + 15, D + 5, z + 15, M('dirt_1'));
    b.leafyTree(x + 8, D + 5, z + 8, 16, 7, M('leaves_2'));
  }
  // Stairs from B's roof up to A's roof in the alley between them (see alleyStairs).
  alleyStairs(b, { x0: 126, x1: 146, zf: 89, low: B, high: A });
  antenna(b, 40, A, 40, 60);
  acUnit(b, 90, A, 30);
  acUnit(b, 170, B, 30);
  acUnit(b, 230, B, 70);
  neonSign(b, 150, G + 46, 109, 's', 'cup', M('neon_cyan'));
  neonSign(b, 60, G + 100, 119, 's', 'star', M('neon_yellow'));
  neonSign(b, 289, G + 48, 60, 'e', 'note', M('neon_pink'), 3);

  // West block: tall tower E, mid F with the avenue bridge, arcade G.
  const E = tower(b, { x0: 16, z0: 250, x1: 110, z1: 390, floors: 4, wall: blocks([M('concrete_1')], 5, 0), trim: M('steel_dark_1'), doors: [{ side: 'n', w: 12 }, { side: 's', w: 12 }] });
  const F = tower(b, { x0: 130, z0: 250, x1: 290, z1: 332, floors: 2, parapet: 2, wall: blocks([M('brick_yellow')], 3, 0), trim: M('stone_4'), doors: [{ side: 'n', w: 12 }, { side: 'e', w: 12 }], hatch: 's' });
  tower(b, { x0: 130, z0: 342, x1: 290, z1: 390, floors: 1, wall: solid([M('paint_indigo')]), trim: M('steel_dark_2'), doors: [{ side: 's', w: 16 }, { side: 'e', w: 12 }] });
  neonSign(b, 190, G + 26, 389, 's', 'game', M('neon_green'), 2);
  b.box(128, G + 29, 390, 292, G + 30, 392, M('neon_purple'));
  // Stairs from F's roof up to E's roof in the alley (see alleyStairs).
  alleyStairs(b, { x0: 110, x1: 130, zf: 311, low: F, high: E });
  billboard(b, 24, E, 300, 70, M('paint_black'), 'rocket', M('neon_cyan'));
  acUnit(b, 60, E, 360);
  acUnit(b, 250, F, 270);
  neonSign(b, 109, G + 104, 330, 'e', 'bolt', M('neon_yellow'), 3);
  neonSign(b, 289, G + 48, 300, 'e', 'heart', M('neon_pink'), 3);
  neonSign(b, 200, G + 50, 250, 'n', 'planet', M('neon_purple'), 2);

  // South-west block: H (team spawn roof), I (water tank), J.
  const H = tower(b, { x0: 16, z0: 450, x1: 140, z1: 624, floors: 2, parapet: 16, wall: blocks([M('brick_orange')], 3, 0), trim: M('concrete_4'), doors: [{ side: 'n', w: 12 }, { side: 'e', w: 12 }], hatch: 'n' });
  const I = tower(b, { x0: 160, z0: 450, x1: 290, z1: 540, floors: 2, parapet: 2, wall: solid([M('plaster_1')]), trim: M('steel_dark_1'), doors: [{ side: 'n', w: 12 }, { side: 'e', w: 12 }], hatch: 'n' });
  const J = tower(b, { x0: 160, z0: 550, x1: 290, z1: 624, floors: 2, parapet: 2, wall: blocks([M('brick_gray')], 3, 0), trim: M('stone_4'), doors: [{ side: 'e', w: 12 }], hatch: 's' });
  waterTank(b, 232, I, 500);
  b.tntCrate(214, I, 470, 7);
  acUnit(b, 40, H, 600);
  acUnit(b, 180, J, 600);
  b.box(14, G + 40, 520, 16, H - 6, 560, M('team_red'));
  neonSign(b, 289, G + 48, 510, 'e', 'cat', M('neon_orange'), 3);
  neonSign(b, 100, G + 46, 450, 'n', 'heart', M('neon_red'), 2);

  // Bridges between the 9 m roofs.
  catwalk(b, 204, 188, 220, 250, D);
  catwalk(b, 140, 490, 160, 506, H);
  catwalk(b, 204, 540, 220, 550, I, false);
  catwalk(b, 290, 312, 320, 328, F);
  catwalk(b, 290, 40, 320, 72, B, true);
  catwalk(b, 290, 568, 320, 600, J, true);
  // Break the parapets where the bridges land.
  for (const [x0, z0, x1, z1, y] of [
    [204, 184, 220, 192, D], [204, 248, 220, 254, D], [136, 490, 142, 506, H], [158, 490, 164, 506, H], [204, 536, 220, 542, I],
    [204, 548, 220, 554, J], [286, 312, 292, 328, F], [286, 40, 292, 72, B], [286, 568, 292, 600, J],
  ])
    b.clear(x0, y, z0, x1, y + 20, z1);

  // Street furniture.
  for (const [x, z, dx, dz] of [[60, 200, 0, 1], [180, 200, 0, 1], [60, 440, 0, -1], [180, 440, 0, -1], [300, 100, 1, 0], [300, 520, 1, 0]]) streetLamp(b, x, z, dx, dz);
  b.car(40, G, 224, M('paint_red'), true);
  b.car(230, G, 204, M('paint_teal'), true);
  b.car(302, G, 86, M('paint_yellow'), false);
  for (const [x, z] of [[140, 196], [252, 244], [292, 290]]) {
    b.box(x, G, z, x + 6, G + 8, z + 6, M('steel_dark_2'));
    b.box(x - 1, G + 8, z - 1, x + 7, G + 9, z + 7, M('steel_dark_1'));
  }
  b.tntBarrel(140, G, 395);
  // Backdrop towers closing the street and avenue vistas (dark facades with a few lit windows).
  const backdrop = (x0: number, z0: number, x1: number, z1: number, hh: number, face: 'e' | 's' | 'n'): void => {
    b.box(x0, G, z0, x1, hh, z1, M('steel_dark_2'));
    for (let y = G + 20; y < hh - 10; y += 16) {
      if (face === 'e') {
        for (let z = z0 + 4; z < z1 - 6; z += 10) if (hash32(y, z, 11) % 4 === 0) b.box(x1, y, z, x1 + 1, y + 6, z + 4, M('neon_yellow'));
      } else {
        const zw = face === 's' ? z1 : z0 - 1;
        for (let x = x0 + 4; x < x1 - 6; x += 10) if (hash32(y, x, 13) % 4 === 0) b.box(x, y, zw, x + 4, y + 6, zw + 1, M('neon_orange'));
      }
    }
  };
  backdrop(0, 176, 8, 264, 170, 'e');
  backdrop(0, 380, 8, 460, 190, 'e');
  backdrop(250, 0, 320, 8, 180, 's');
  backdrop(250, S - 8, 320, S, 160, 'n');
}

function build(b: MapBuilder): MapMeta {
  b.ground(G, M('concrete_2'), M('dirt_2'), M('rock_1'));
  westHalf(b);
  b.symmetrize(SYM);
  // Median planters down the avenue (straddle the center line).
  for (const z of [40, 150, 460, 560]) {
    b.box(312, G, z, 328, G + 5, z + 40, M('concrete_1'));
    b.box(313, G + 4, z + 1, 327, G + 5, z + 39, M('dirt_1'));
    b.box(314, G + 5, z + 4, 326, G + 10, z + 36, M('leaves_1'));
  }

  // Metadata ------------------------------------------------------------------------------
  const gy = G / 10;
  const meta = emptyMeta(gy, 32, 32);
  const hy = roofY(2) / 10;
  const team0: [number, number][] = [
    [3.5, 48.5], [6, 48.5], [8.5, 48.5], [11, 48.5], [3.5, 51.5], [6, 51.5], [8.5, 51.5], [11, 51.5],
  ];
  for (const [x, z] of team0) {
    const yaw = yawToward(x, z, 32, 32);
    meta.spawns.push({ team: 0, x, y: hy, z, yaw });
    const [mx, mz] = symPoint(SYM, S / 10, S / 10, x, z);
    meta.spawns.push({ team: 1, x: mx, y: hy, z: mz, yaw: symYaw(SYM, yaw) });
  }
  // FFA spawns on the 9.3 m roofs and in the streets, each with its rotated twin.
  const roof3 = roofY(2) / 10 + 0.5;
  const street = gy + 0.5;
  const ffaHalf: [number, number, number][] = [
    [20, 5, roof3], [26, 7, roof3], [21, 14.5, roof3], [16, 30, roof3], [25, 29, roof3], [17.5, 53, roof3], [26, 58, roof3],
    [5, 22, street], [20, 22, street], [30.5, 35, street],
  ];
  for (const [x, z, from] of ffaHalf) {
    meta.spawns.push({ team: -1, x, y: b.standAt(x, z, from), z, yaw: yawToward(x, z, 32, 32) });
    const [mx, mz] = symPoint(SYM, S / 10, S / 10, x, z);
    meta.spawns.push({ team: -1, x: mx, y: b.standAt(mx, mz, from), z: mz, yaw: yawToward(mx, mz, 32, 32) });
  }
  for (const [x, z, from, kind] of [[20, 31, roof3, 'health'], [29, 22, street, 'armor'], [25, 54.5, street, 'health'], [8, 58, roof3, 'health']] as const) {
    meta.pickups.push({ kind, x, y: b.standAt(x, z, from), z });
    const [mx, mz] = symPoint(SYM, S / 10, S / 10, x, z);
    meta.pickups.push({ kind, x: mx, y: b.standAt(mx, mz, from), z: mz });
  }
  return meta;
}

export const neonRooftops: MapDef = {
  id: 'neon_rooftops',
  name: 'Neon Rooftops',
  description: 'Night over a glowing city block. Fight across the roofs, the catwalks and the avenue below.',
  size: [S, 256, S],
  modes: ['ffa', 'tdm', 'gungame', 'sandbox', 'practice'],
  players: [4, 12],
  seed: 8080,
  version: 2,
  theme: {
    skyTop: 0x070a1f,
    skyHorizon: 0x2c1c4a,
    fog: 0x1d1633,
    fogNear: 30,
    fogFar: 125,
    sun: [-0.35, 0.75, -0.45],
    sunColor: 0x9aa6ff,
    sunIntensity: 0.4,
    ambient: 0x5a64a8,
    ambientIntensity: 0.48,
    outside: 0x16141e,
    music: 'neon',
    night: true,
  },
  build,
};
